import { api } from '../lib/api.js';
import type {
  AppLockConfigInput,
  AppLockStatus,
  CompanyInfo,
  CompanySettings,
  SettingsCategory,
  WorkdayEngineStatus,
} from '@attendance/shared/schemas/settings';

export type {
  AppLockStatus,
  CompanyInfo,
  CompanySettings,
  SettingsCategory,
  WorkdayEngineStatus,
};

/**
 * Is `faceRecognition.fieldReviewMode` actually doing what its label says?
 *
 * `verify` re-runs the face match SERVER-side before a punch counts, which
 * needs a descriptor from the device — and no shipped field client uploads one.
 * So an admin can select it, save it, and read it back from `/settings/all`
 * forever while it has no effect on anything. Exactly the `WorkdayEngineStatus`
 * shape: a derived fact the settings payload cannot express, because the
 * payload's job is to report configuration and this is about whether the
 * configuration is running.
 *
 * `reason` is SERVER-AUTHORED prose and is rendered verbatim. It has to be:
 * the thing that makes `verify` inoperative is a property of the code that
 * ships, and the server is the half that knows. A client paraphrase would be a
 * second statement of one fact — the divergence design-rules §3 is about — and
 * would go stale the day a client does start uploading a descriptor.
 */
export interface FaceReviewModeStatus {
  /** The stored mode, echoed back: `auto_count` | `verify`. */
  mode: string;
  /** True ONLY when `verify` is selected and nothing can satisfy it. */
  inoperative: boolean;
  /** Human-readable, server-authored. Render it; do not rewrite it. */
  reason: string;
}

export const appLockApi = {
  status: () =>
    api.get<{ data: AppLockStatus }>('/settings/app-lock').then((r) => r.data),
  configure: (input: AppLockConfigInput) =>
    api.patch<{ data: AppLockStatus }>('/settings/app-lock', input).then((r) => r.data),
  verify: (scope: 'open' | 'admin', pin: string) =>
    api
      .post<{ data: { ok: boolean } }>('/settings/app-lock/verify', { scope, pin })
      .then((r) => r.data.ok),
};

export const settingsApi = {
  getAll: () => api.get<{ data: CompanySettings }>('/settings/all').then((r) => r.data),
  /**
   * Is the flexible net-hours + overtime engine actually running?
   *
   * This is a SEPARATE request on purpose, and it is the only way a client can
   * know. `/settings/all` returns `CompanySettingsSchema.parse(stored)`, and
   * `DefaultWorkdaySchema` carries `.default(9)` / `.default(30)` — so a company
   * with no `defaultWorkday` block stored at all comes back byte-identical to
   * one that configured those exact numbers deliberately. The rollup branches on
   * the RAW stored JSON, so it sees a difference the payload cannot express.
   * There is no client-side test for this, at any cost; the server has to say.
   */
  workdayStatus: () =>
    api
      .get<{ data: WorkdayEngineStatus }>('/settings/workday-status')
      .then((r) => r.data),
  /**
   * Is `fieldReviewMode: 'verify'` actually running? Separate request for the
   * same reason `workdayStatus` is: `/settings/all` reports the stored mode as
   * configuration and has no way to say "selected, but inert". See
   * `FaceReviewModeStatus`.
   */
  faceReviewStatus: () =>
    api
      .get<{ data: FaceReviewModeStatus }>('/settings/face-review-status')
      .then((r) => r.data),
  getInfo: () => api.get<{ data: CompanyInfo & { logoUrl?: string | null } }>('/settings/info').then((r) => r.data),
  updateInfo: (input: CompanyInfo) =>
    api.patch<{ data: unknown }>('/settings/info', input),
  getCategory: <K extends SettingsCategory>(category: K) =>
    api
      .get<{ data: CompanySettings[K] }>(`/settings/${category}`)
      .then((r) => r.data),
  updateCategory: <K extends SettingsCategory>(category: K, value: CompanySettings[K]) =>
    api.patch<{ data: CompanySettings[K] }>(`/settings/${category}`, value).then((r) => r.data),
};
